import React, { useContext, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { DataContext } from '../context/DataProvider';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, ArrowLeft, BookOpen, Share2, Tag } from 'lucide-react';
import { supabase } from '../supabase';

import { defaultBlogs } from '../data/defaultBlogs';

const BlogPostDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { blogs, loading: contextLoading } = useContext(DataContext);
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedPosts, setRelatedPosts] = useState([]);

    useEffect(() => {
        const fetchPost = async () => {
            setLoading(true);
            const allPosts = blogs && blogs.length > 0 ? blogs : defaultBlogs;
            // 1. Try context state / defaultBlogs first
            let currentPost = allPosts.find(b => b.slug === slug);

            // 2. If not found, check defaultBlogs directly
            if (!currentPost) {
                currentPost = defaultBlogs.find(b => b.slug === slug);
            }

            // 3. If not found in defaultBlogs, fetch from Supabase directly
            if (!currentPost) {
                try {
                    const { data, error } = await supabase
                        .from('pyrusmedia_blogs')
                        .select('*')
                        .eq('slug', slug)
                        .single();

                    if (data && !error) {
                        currentPost = data;
                    }
                } catch (err) {
                    console.error('Error fetching blog post:', err);
                }
            }

            if (currentPost) {
                setPost(currentPost);
                // Find related posts
                const related = allPosts.filter(b => b.id !== currentPost.id).slice(0, 3);
                setRelatedPosts(related);
            } else {
                setPost(null);
            }
            setLoading(false);
        };

        fetchPost();
    }, [slug, blogs]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    if (loading || contextLoading) {
        return (
            <div className="min-h-screen pt-32 pb-20 bg-white flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#bebd19] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-500 font-medium">Loading article...</p>
                </div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="min-h-screen pt-32 pb-20 bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md px-4">
                    <div className="w-16 h-16 bg-gray-200 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                        <BookOpen size={32} />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Article Not Found</h2>
                    <p className="text-gray-500 mb-6">The article you are looking for does not exist or has been removed.</p>
                    <Button onClick={() => navigate('/blog')} className="inline-flex items-center gap-2">
                        <ArrowLeft size={16} /> Back to Blog
                    </Button>
                </div>
            </div>
        );
    }

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `https://pyrusmedia.in/blog/${post.slug || slug}#breadcrumb`,
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://pyrusmedia.in/"
            },
            {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://pyrusmedia.in/blog"
            },
            {
                "@type": "ListItem",
                "position": 3,
                "name": post.title,
                "item": `https://pyrusmedia.in/blog/${post.slug || slug}`
            }
        ]
    };

    return (
        <>
            <Helmet>
                <title>{post.title} | PyrusMedia Blog</title>
                <meta name="description" content={post.excerpt || post.title} />
                <meta property="og:title" content={post.title} />
                <meta property="og:description" content={post.excerpt} />
                {post.cover_image && <meta property="og:image" content={post.cover_image} />}
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                </script>
            </Helmet>

            <article className="pt-28 pb-20 bg-white overflow-hidden w-full">
                {/* Header */}
                <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden break-words">
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-[#bebd19] transition-colors mb-6"
                    >
                        <ArrowLeft size={16} /> Back to Insights
                    </Link>

                    {post.category && (
                        <div className="mb-4">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#bebd19]/20 text-gray-900 uppercase tracking-wider">
                                <Tag size={12} /> {post.category}
                            </span>
                        </div>
                    )}

                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6 break-words [word-break:break-word]">
                        {post.title}
                    </h1>

                    {post.excerpt && (
                        <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8 border-l-4 border-[#bebd19] pl-4 italic break-words [word-break:break-word]">
                            {post.excerpt}
                        </p>
                    )}

                    {/* Metadata bar */}
                    <div className="flex flex-wrap items-center justify-between gap-4 py-4 border-y border-gray-100 text-sm text-gray-500 mb-10">
                        <div className="flex flex-wrap items-center gap-6">
                            <div className="flex items-center gap-2 font-medium text-gray-900">
                                <div className="w-8 h-8 rounded-full bg-[#bebd19] text-gray-900 flex items-center justify-center font-bold text-xs">
                                    {(post.author || 'Pyrus')[0]}
                                </div>
                                <span>{post.author || 'PyrusMedia Team'}</span>
                            </div>

                            <div className="flex items-center gap-1.5">
                                <Calendar size={16} />
                                <span>{formatDate(post.created_at)}</span>
                            </div>

                            {post.read_time && (
                                <div className="flex items-center gap-1.5">
                                    <Clock size={16} />
                                    <span>{post.read_time}</span>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => {
                                if (navigator.share) {
                                    navigator.share({ title: post.title, url: window.location.href });
                                } else {
                                    navigator.clipboard.writeText(window.location.href);
                                    alert('Article link copied to clipboard!');
                                }
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:border-[#bebd19] hover:text-gray-900 transition-colors"
                        >
                            <Share2 size={16} /> Share
                        </button>
                    </div>

                    {/* Featured Cover Image */}
                    {post.cover_image && (
                        <div className="mb-12 rounded-2xl overflow-hidden shadow-lg border border-gray-100 max-h-[480px]">
                            <img
                                src={post.cover_image}
                                alt={post.title}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    {/* Content Body */}
                    <div
                        className="blog-content w-full text-gray-800 leading-relaxed break-words [word-break:break-word] [overflow-wrap:anywhere]"
                        dangerouslySetInnerHTML={{ __html: post.content || '<p>No content written yet.</p>' }}
                    />
                </div>
            </article>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
                <Section className="bg-gray-50 py-16 border-t border-gray-200">
                    <div className="container mx-auto px-4 max-w-5xl">
                        <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">More Articles You Might Like</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {relatedPosts.map(rel => (
                                <Link
                                    key={rel.id}
                                    to={`/blog/${rel.slug}`}
                                    className="bg-white rounded-xl p-5 border border-gray-200 hover:border-[#bebd19] transition-all flex flex-col group"
                                >
                                    <span className="text-xs font-bold text-[#bebd19] uppercase tracking-wider mb-2">
                                        {rel.category}
                                    </span>
                                    <h3 className="text-base font-bold text-gray-900 group-hover:text-[#bebd19] transition-colors mb-2 line-clamp-2">
                                        {rel.title}
                                    </h3>
                                    <p className="text-gray-500 text-xs line-clamp-2 mt-auto">
                                        {rel.excerpt}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </Section>
            )}
        </>
    );
};

export default BlogPostDetail;
